import type React from "react";
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import {
	authService,
	NewPasswordRequiredError,
} from "../services/auth/authService";
import type { User } from "../types";

interface AuthContextType {
	user: User | null;
	isAuthenticated: boolean;
	isAdmin: boolean;
	isStellarAdmin: boolean;
	/** True for admin OR stellar_admin — has dashboard access */
	hasAdminAccess: boolean;
	isPartner: boolean;
	loading: boolean;
	newPasswordRequired: boolean;
	login: (email: string, password: string) => Promise<void>;
	logout: () => Promise<void>;
	signup: (email: string, password: string, name: string) => Promise<void>;
	completeNewPassword: (newPassword: string) => Promise<void>;
	forgotPassword: (email: string) => Promise<void>;
	confirmForgotPassword: (
		email: string,
		code: string,
		newPassword: string,
	) => Promise<void>;
	checkAccess: (walletId: string) => boolean;
	refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [user, setUser] = useState<User | null>(null);
	const [loading, setLoading] = useState(true);
	const [newPasswordRequired, setNewPasswordRequired] = useState(false);

	const checkUser = useCallback(async () => {
		try {
			const currentUser = await authService.getCurrentUser();
			setUser(currentUser);
		} catch (_error) {
			setUser(null);
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		checkUser();
	}, [checkUser]);

	const login = useCallback(async (email: string, password: string) => {
		setLoading(true);
		try {
			const loggedInUser = await authService.login(email, password);
			setNewPasswordRequired(false);
			setUser(loggedInUser);
		} catch (err) {
			if (err instanceof NewPasswordRequiredError) {
				setNewPasswordRequired(true);
			}
			throw err; // Re-throw so Login page can display the error/form
		} finally {
			setLoading(false);
		}
	}, []);

	const completeNewPassword = useCallback(async (newPassword: string) => {
		setLoading(true);
		try {
			if (authService.completeNewPassword) {
				const loggedInUser = await authService.completeNewPassword(newPassword);
				setNewPasswordRequired(false);
				setUser(loggedInUser);
			} else {
				throw new Error("Password change not supported");
			}
		} finally {
			setLoading(false);
		}
	}, []);

	const logout = useCallback(async () => {
		await authService.logout();
		setUser(null);
	}, []);

	const signup = useCallback(
		async (email: string, password: string, name: string) => {
			setLoading(true);
			try {
				const newUser = await authService.signup(email, password, name);
				setUser(newUser);
			} finally {
				setLoading(false);
			}
		},
		[],
	);

	const checkAccess = useCallback(
		(walletId: string): boolean => {
			if (!user) return false;
			if (user.role === "admin" || user.role === "stellar_admin") return true;
			return (user.wallet_ids ?? []).includes(walletId);
		},
		[user],
	);

	const refreshUser = useCallback(async () => {
		await checkUser();
	}, [checkUser]);

	const forgotPassword = useCallback(async (email: string) => {
		if (authService.forgotPassword) {
			await authService.forgotPassword(email);
		} else {
			throw new Error("Forgot password not supported");
		}
	}, []);

	const confirmForgotPassword = useCallback(
		async (email: string, code: string, newPassword: string) => {
			if (authService.confirmForgotPassword) {
				await authService.confirmForgotPassword(email, code, newPassword);
			} else {
				throw new Error("Confirm password reset not supported");
			}
		},
		[],
	);

	const isAdmin = user?.role === "admin";
	const isStellarAdmin = user?.role === "stellar_admin";
	const hasAdminAccess = isAdmin || isStellarAdmin;

	const contextValue = useMemo(
		() => ({
			user,
			isAuthenticated: !!user,
			isAdmin: isAdmin ?? false,
			isStellarAdmin: isStellarAdmin ?? false,
			hasAdminAccess: hasAdminAccess ?? false,
			isPartner: user?.role === "partner",
			loading,
			newPasswordRequired,
			login,
			logout,
			signup,
			completeNewPassword,
			forgotPassword,
			confirmForgotPassword,
			checkAccess,
			refreshUser,
		}),
		[
			user,
			isAdmin,
			isStellarAdmin,
			hasAdminAccess,
			loading,
			newPasswordRequired,
			login,
			logout,
			signup,
			completeNewPassword,
			forgotPassword,
			confirmForgotPassword,
			checkAccess,
			refreshUser,
		],
	);

	return (
		<AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
	);
};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within AuthProvider");
	}
	return context;
};
